import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import { Icon } from "./icons";
import { getGuide, guideKeyForPath } from "./guideContent";

const HelpGlyph = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M9.1 9a3 3 0 1 1 4.5 2.6c-.8.5-1.6 1.1-1.6 2.1v.4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="12" cy="17.4" r="1.2" fill="currentColor" />
    <circle cx="12" cy="12" r="9.2" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);

/**
 * "Uitleg" (help) floating action button + per-screen explanation side panel.
 * Mounted once in the Layout so it appears on every authenticated screen; the
 * content follows the current route.
 *
 * The FAB is exactly button-sized — no oversized transparent hit area — so it
 * only swallows clicks on the button itself (audit 2.6.4, Fitts's Law). Pages
 * reserve bottom padding so their last row clears it.
 */
export function UitlegPanel() {
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const navy = theme.palette.brand.navy;
  const guideMap = getGuide(t);
  const guide = guideMap[guideKeyForPath(location.pathname)] || guideMap.dashboard;

  return (
    <>
      {/* Floating action button */}
      {!open && (
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          title={t("guide.panel.openTooltip")}
          sx={{
            position: "fixed",
            right: "26px",
            bottom: "26px",
            zIndex: 1150,
            // Shrink-wrap the button: the hit area is the button, nothing more.
            width: "auto",
            height: "auto",
            display: "flex",
            alignItems: "center",
            gap: "9px",
            background: theme.palette.brand.icon,
            color: theme.palette.common.white,
            border: "none",
            borderRadius: 999,
            padding: "13px 20px 13px 17px",
            fontFamily: "inherit",
            fontSize: 14.5,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: `0 10px 26px ${alpha(navy, 0.3)},0 2px 6px ${alpha(navy, 0.18)}`,
            transition: "background 150ms, transform 150ms, box-shadow 150ms",
            "&:hover": {
              background: darken(theme.palette.brand.icon, 0.12),
              transform: "translateY(-2px)",
              boxShadow: `0 16px 34px ${alpha(navy, 0.34)},0 3px 8px ${alpha(navy, 0.2)}`,
            },
          }}
        >
          <HelpGlyph />
          <span>{t("guide.panel.label")}</span>
        </Box>
      )}

      {/* Panel + scrim */}
      {open && (
        <>
          <Box
            onClick={() => setOpen(false)}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1250,
              background: alpha(navy, 0.2),
              animation: "guideScrim 160ms ease",
            }}
          />
          <Box
            component="aside"
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 1251,
              width: 404,
              maxWidth: "88%",
              background: theme.palette.common.white,
              borderLeft: `1px solid ${theme.palette.border.strong}`,
              boxShadow: `-18px 0 44px ${alpha(navy, 0.16)}`,
              display: "flex",
              flexDirection: "column",
              animation: "guideSlide 220ms cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            {/* header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "14px",
                padding: "24px 24px 18px",
                borderBottom: `1px solid ${theme.palette.border.subtle}`,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 42,
                  height: 42,
                  flex: "none",
                  borderRadius: "12px",
                  background: theme.palette.surface.cyan,
                  color: theme.palette.brand.icon,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HelpGlyph size={22} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.09em",
                    color: theme.palette.status.runningFg,
                    textTransform: "uppercase",
                  }}
                >
                  {t("guide.panel.label")}
                </Box>
                <Box
                  component="h2"
                  sx={{
                    margin: "2px 0 0",
                    fontSize: 20,
                    fontWeight: 800,
                    letterSpacing: "-0.01em",
                    color: navy,
                    lineHeight: 1.2,
                  }}
                >
                  {guide.title}
                </Box>
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setOpen(false)}
                title={t("common.close")}
                sx={{
                  flex: "none",
                  width: 34,
                  height: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  background: "none",
                  borderRadius: "9px",
                  color: theme.palette.text.secondary,
                  cursor: "pointer",
                  transition: "background 140ms,color 140ms",
                  "&:hover": {
                    background: theme.palette.border.soft,
                    color: navy,
                  },
                }}
              >
                <Icon name="close" size={19} />
              </Box>
            </Box>

            {/* body */}
            <Box sx={{ flex: 1, overflowY: "auto", padding: "22px 24px 28px" }}>
              <Box
                component="p"
                sx={{
                  margin: "0 0 22px",
                  fontSize: 15,
                  lineHeight: 1.6,
                  color: theme.palette.text.secondary,
                }}
              >
                {guide.purpose}
              </Box>

              <Box
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: theme.palette.status.runningFg,
                  textTransform: "uppercase",
                  marginBottom: "12px",
                }}
              >
                {t("guide.panel.canHeading")}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "11px",
                  marginBottom: "26px",
                }}
              >
                {guide.can.map((item, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: "11px" }}>
                    <Box
                      component="span"
                      sx={{
                        flex: "none",
                        marginTop: "2px",
                        color: theme.palette.brand.icon,
                        display: "flex",
                      }}
                    >
                      <Icon name="check" size={18} />
                    </Box>
                    <Box component="span" sx={{ flex: 1, fontSize: 14, lineHeight: 1.5, color: navy }}>
                      {item}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: theme.palette.status.runningFg,
                  textTransform: "uppercase",
                  marginBottom: "14px",
                }}
              >
                {t("guide.panel.stepsHeading")}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", marginBottom: "26px" }}>
                {guide.steps.map((text, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "13px",
                      paddingBottom: "16px",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "none",
                        width: 27,
                        height: 27,
                        borderRadius: 999,
                        background: theme.palette.brand.navy,
                        color: theme.palette.common.white,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 800,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        paddingTop: "3px",
                        fontSize: 14,
                        lineHeight: 1.5,
                        color: navy,
                      }}
                    >
                      {text}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "11px",
                  background: theme.palette.surface.cyan,
                  border: `1px solid ${theme.palette.border.cyan}`,
                  borderRadius: "12px",
                  padding: "14px 15px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    flex: "none",
                    marginTop: "1px",
                    color: theme.palette.status.runningFg,
                    display: "flex",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.6.6 1 1.2 1 2V16h6v-.5c0-.8.4-1.4 1-2A6 6 0 0 0 12 3z"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: theme.palette.status.runningFg,
                      marginBottom: "2px",
                    }}
                  >
                    {t("guide.panel.tipLabel")}
                  </Box>
                  <Box
                    sx={{
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      color: theme.palette.status.runningFg,
                    }}
                  >
                    {guide.tip}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );
}
